// src/components/auth/ResetPassword.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { resetPassword, verifyResetToken } from '../../features/auth/authSlice';

const ResetPassword = () => {
    const { token } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });

    const [tokenValid, setTokenValid] = useState(true);
    const [tokenChecked, setTokenChecked] = useState(false);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    useEffect(() => {
        const checkToken = async () => {
            try {
                await dispatch(verifyResetToken({ token })).unwrap();
                setTokenValid(true);
            } catch (error) {
                setTokenValid(false);
            } finally {
                setTokenChecked(true);
            }
        };

        checkToken();
    }, [dispatch, token]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });

        // Clear error when field is edited
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        const newErrors = {};

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        }

        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsSubmitting(true);

        try {
            await dispatch(resetPassword({
                token,
                password: formData.password
            })).unwrap();

            setIsSuccess(true);

            // Redirect to login after 3 seconds
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (error) {
            setErrors({
                ...errors,
                general: error.message || 'An error occurred. Please try again.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Show loading state while checking token
    if (!tokenChecked) {
        return (
            <div className="auth-container">
                <div className="auth-form-container">
                    <div className="loading-message">Verifying reset link...</div>
                </div>
            </div>
        );
    }

    // If token is invalid
    if (!tokenValid) {
        return (
            <div className="auth-container">
                <div className="auth-form-container">
                    <div className="auth-message-box error">
                        <h1>Invalid or Expired Link</h1>
                        <p>
                            The password reset link is invalid or has expired.
                            Please request a new password reset link.
                        </p>
                        <Link to="/forgot-password" className="btn btn-primary">
                            Request New Link
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // If password reset was successful
    if (isSuccess) {
        return (
            <div className="auth-container">
                <div className="auth-form-container">
                    <div className="auth-message-box success">
                        <h1>Password Reset Successfully</h1>
                        <p>
                            Your password has been reset successfully. You will be redirected to the login page.
                        </p>
                        <Link to="/login" className="btn btn-primary">
                            Sign In Now
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-container">
            <div className="auth-form-container">
                <h1>Create New Password</h1>
                <p className="auth-subtitle">
                    Please enter your new password below.
                </p>

                {errors.general && <div className="error-message">{errors.general}</div>}

                <form onSubmit={handleSubmit} className="auth-form">
                    <div className="form-group">
                        <label htmlFor="password">New Password</label>
                        <input
                            type="password"
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            className={errors.password ? 'error' : ''}
                        />
                        {errors.password && <div className="error-text">{errors.password}</div>}
                        <div className="password-requirements">
                            Password must be at least 8 characters
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirmPassword">Confirm New Password</label>
                        <input
                            type="password"
                            id="confirmPassword"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            className={errors.confirmPassword ? 'error' : ''}
                        />
                        {errors.confirmPassword && (
                            <div className="error-text">{errors.confirmPassword}</div>
                        )}
                    </div>

                    <button
                        type="submit"
                        className="btn btn-primary btn-block"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Updating Password...' : 'Reset Password'}
                    </button>
                </form>
            </div>

            <div className="auth-info">
                <h2>Create a Strong Password</h2>
                <div className="password-tips">
                    <h3>Tips for a secure password:</h3>
                    <ul>
                        <li>Use at least 8 characters</li>
                        <li>Include uppercase and lowercase letters</li>
                        <li>Add numbers and special characters</li>
                        <li>Avoid using common words or personal information</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
